import React, { useState } from 'react';
import { X, Calendar, Building2, MapPin, User, Mail, Phone, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import api from '@/utils/api';
import { SmsOptInCheckbox } from './SmsConsentDisclosure';

const TourRequestModal = ({ isOpen, onClose, builderName, communityName, builders }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    builder_name: builderName || '',
    community_name: communityName || '',
    preferred_date: '',
    preferred_time: '',
    message: '',
    sms_opt_in: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when modal opens with new builder/community
  React.useEffect(() => {
    if (isOpen) {
      setFormData(prev => ({
        ...prev,
        builder_name: builderName || prev.builder_name,
        community_name: communityName || prev.community_name
      }));
    }
  }, [isOpen, builderName, communityName]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await api.post('/tour-request', formData);
      toast.success('Tour request submitted! We\'ll contact you to confirm your appointment.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        builder_name: '',
        community_name: '',
        preferred_date: '',
        preferred_time: '',
        message: ''
      });
      onClose();
    } catch (error) {
      toast.error('Failed to submit request. Please try again.');
      console.error('Tour request error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // Get tomorrow's date as minimum date
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  // Get communities for selected builder
  const selectedBuilder = builders?.find(b => b.name === formData.builder_name);
  const communities = selectedBuilder?.communities || [];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="tour-modal" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 transition-opacity" 
        onClick={onClose}
        aria-hidden="true"
      ></div>

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div 
          className="relative w-full max-w-lg bg-white rounded-2xl shadow-xl transform transition-all"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#BCA480] to-[#a89470] px-6 py-5 rounded-t-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <Calendar className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white" id="tour-modal">Schedule a Tour</h2>
                  <p className="text-white/80 text-sm">Visit a new construction community</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-1 rounded-full hover:bg-white/20 transition-colors"
                aria-label="Close modal"
              >
                <X className="h-5 w-5 text-white" />
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Name & Email */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="tour-name" className="flex items-center gap-2 mb-1.5 text-sm font-medium">
                  <User className="h-3.5 w-3.5 text-[#BCA480]" />
                  Full Name *
                </Label>
                <Input
                  id="tour-name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your name"
                  required
                  data-testid="tour-name-input"
                />
              </div>
              <div>
                <Label htmlFor="tour-email" className="flex items-center gap-2 mb-1.5 text-sm font-medium">
                  <Mail className="h-3.5 w-3.5 text-[#BCA480]" />
                  Email *
                </Label>
                <Input
                  id="tour-email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@email.com"
                  required
                  data-testid="tour-email-input"
                />
              </div>
            </div>

            {/* Phone */}
            <div>
              <Label htmlFor="tour-phone" className="flex items-center gap-2 mb-1.5 text-sm font-medium">
                <Phone className="h-3.5 w-3.5 text-[#BCA480]" />
                Phone Number *
              </Label>
              <Input
                id="tour-phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="(901) 555-0123"
                required
                data-testid="tour-phone-input"
              />
              <SmsOptInCheckbox
                checked={formData.sms_opt_in}
                onChange={(v) => setFormData({ ...formData, sms_opt_in: v })}
              />
            </div>

            {/* Builder & Community */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-2 mb-1.5 text-sm font-medium">
                  <Building2 className="h-3.5 w-3.5 text-[#BCA480]" />
                  Builder *
                </Label>
                <Select 
                  value={formData.builder_name} 
                  onValueChange={(value) => handleSelectChange('builder_name', value)}
                  required
                >
                  <SelectTrigger data-testid="tour-builder-select">
                    <SelectValue placeholder="Select a builder" />
                  </SelectTrigger>
                  <SelectContent>
                    {builders?.map((builder) => (
                      <SelectItem key={builder.id} value={builder.name}>
                        {builder.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="flex items-center gap-2 mb-1.5 text-sm font-medium">
                  <MapPin className="h-3.5 w-3.5 text-[#BCA480]" />
                  Community
                </Label>
                <Select 
                  value={formData.community_name} 
                  onValueChange={(value) => handleSelectChange('community_name', value)}
                  disabled={!formData.builder_name}
                >
                  <SelectTrigger data-testid="tour-community-select">
                    <SelectValue placeholder={formData.builder_name ? "Select community" : "Select builder first"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any Community</SelectItem>
                    {communities.map((community, idx) => (
                      <SelectItem key={idx} value={community.name}>
                        {community.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="tour-date" className="flex items-center gap-2 mb-1.5 text-sm font-medium">
                  <Calendar className="h-3.5 w-3.5 text-[#BCA480]" />
                  Preferred Date *
                </Label>
                <Input
                  id="tour-date"
                  name="preferred_date"
                  type="date"
                  value={formData.preferred_date}
                  onChange={handleChange}
                  min={minDate}
                  required
                  data-testid="tour-date-input"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2 mb-1.5 text-sm font-medium">
                  Preferred Time
                </Label>
                <Select 
                  value={formData.preferred_time} 
                  onValueChange={(value) => handleSelectChange('preferred_time', value)}
                >
                  <SelectTrigger data-testid="tour-time-select">
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="morning">Morning (9am - 12pm)</SelectItem>
                    <SelectItem value="afternoon">Afternoon (12pm - 4pm)</SelectItem>
                    <SelectItem value="evening">Evening (4pm - 7pm)</SelectItem>
                    <SelectItem value="flexible">Flexible</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Message */}
            <div>
              <Label htmlFor="tour-message" className="flex items-center gap-2 mb-1.5 text-sm font-medium">
                <MessageSquare className="h-3.5 w-3.5 text-[#BCA480]" />
                Message (Optional)
              </Label>
              <Textarea
                id="tour-message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Any specific questions or preferences for your tour?"
                rows={3}
                data-testid="tour-message-input"
              />
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#BCA480] hover:bg-[#a89470] text-white py-3"
              data-testid="tour-submit-btn"
            >
              {isSubmitting ? 'Submitting...' : 'Request Tour'}
            </Button>

            <p className="text-xs text-gray-500 text-center">
              We&apos;ll contact you within 24 hours to confirm your tour.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TourRequestModal;
